import React, { useState, useCallback, Children, type ReactNode } from 'react';
import noop from 'lodash/noop';
import { Box, xcss } from '@atlaskit/primitives';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import { IconButton } from '@atlaskit/button/new';
import { ExitingPersistence, SlideIn } from '@atlaskit/motion';
import ImageIcon from '@atlaskit/icon/core/image';
import { AiWorkCreateLinkPicker } from '@atlassian/jira-software-backlog-ai-work-creation/src/view/ai-work-create-link-picker/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import messages from './messages.tsx';

type HoverBoxProps = {
	shouldRenderHoverBox: boolean;
	children: ReactNode;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	onLinkPicked?: (url: string) => void;
	onLinkPickerMounted?: () => void;
};

const HoverBoxNew = ({
	shouldRenderHoverBox,
	children,
	onMouseEnter: onMouseEnterProp,
	onMouseLeave: onMouseLeaveProp,
	onLinkPicked = noop,
	onLinkPickerMounted = noop,
}: HoverBoxProps) => {
	const [isHovering, setIsHovering] = useState(false);
	const [isLinkPickerOpen, setIsLinkPickerOpen] = useState(false);
	const shouldShowTriggerButtons = isHovering || isLinkPickerOpen;

	const { formatMessage } = useIntl();

	const onMouseEnter = useCallback(() => {
		setIsHovering(true);
		onMouseEnterProp();
	}, [onMouseEnterProp]);

	const onMouseLeave = useCallback(() => {
		setIsHovering(false);
		onMouseLeaveProp();
	}, [onMouseLeaveProp]);

	const onCloseLinkPicker = useCallback(() => {
		setIsLinkPickerOpen(false);
		setIsHovering(false);
	}, []);

	const onCreateFromConfluenceButtonClick = useCallback(() => {
		setIsLinkPickerOpen(true);
	}, []);

	// this should never happen - just in case
	if (!shouldRenderHoverBox) {
		return Children.only(children);
	}

	const renderLinkPicker = () => (
		<AiWorkCreateLinkPicker
			onMounted={onLinkPickerMounted}
			onLinkSelected={onLinkPicked}
			onCloseLinkPicker={onCloseLinkPicker}
		/>
	);

	const renderGenerateFromConfluenceButton = ({ ...triggerProps }) => (
		<IconButton
			appearance="subtle"
			label={formatMessage(messages.buttonLabelGenerateFromConfluence)}
			isTooltipDisabled={false}
			icon={WorkItemsIcon}
			onClick={onCreateFromConfluenceButtonClick}
			isSelected={isLinkPickerOpen}
			{...triggerProps}
		/>
	);

	return (
		<Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} xcss={hoverContainerStyles}>
			{Children.only(children)}
			<Box xcss={triggerOverlayStyles}>
				{shouldShowTriggerButtons && (
					<ExitingPersistence appear>
						<SlideIn enterFrom="bottom" fade="in">
							{({ className, ref }) => (
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
								<div className={className} ref={ref}>
									<Popup
										messageId="software-backlog.card-list.inline-card-create.ai-work-create.hover-box.popup.placeholder-with-link-picker.popup"
										messageType="transactional"
										isOpen={isLinkPickerOpen}
										onClose={onCloseLinkPicker}
										content={renderLinkPicker}
										trigger={renderGenerateFromConfluenceButton}
									/>

									{fg('ai_powered_backlog_inline_create_from_image') && (
										<IconButton
											appearance="subtle"
											label={formatMessage(messages.buttonLabelGenerateFromImage)}
											isTooltipDisabled={false}
											icon={ImageIcon}
										/>
									)}
								</div>
							)}
						</SlideIn>
					</ExitingPersistence>
				)}
			</Box>
		</Box>
	);
};

const HoverBoxOld = ({
	children,
	onMouseEnter,
	onMouseLeave,
	shouldRenderHoverBox,
}: HoverBoxProps) =>
	shouldRenderHoverBox ? (
		<Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
			{Children.only(children)}
		</Box>
	) : (
		Children.only(children)
	);

export const HoverBox = (props: HoverBoxProps) =>
	expValEquals('ai_powered_backlog_inline_create_iter2', 'isEnabled', true, false) ? (
		<HoverBoxNew {...props} /> // when in iter 2 experiment Test
	) : (
		<HoverBoxOld {...props} />
	); // when in iter 2 experiment Control, or not in experiment

const hoverContainerStyles = xcss({
	position: 'relative',
	overflow: 'hidden',
});

const triggerOverlayStyles = xcss({
	position: 'absolute',
	top: 'space.0',
	right: 'space.050',
	bottom: 'space.0',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
});
